import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import clsx from "clsx";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
  setIsModalxOpen,
} from "components/modalx/modalx.slice";
import { Heading } from "catalyst-ui-kit/heading";
import { Text } from "catalyst-ui-kit/text";
import { Badge } from "catalyst-ui-kit/badge";
import DeskEmailHtml from "components/desk-email-html/desk-email-html";
import { visibleAttachments } from "components/desk-email-html/desk-email-cids";
import DeskMeetingBanner from "components/desk-meeting-banner/desk-meeting-banner";
import DeskShareUserPicker from "components/desk-share-user-picker/desk-share-user-picker";
import DeskLinkCrmProjectPopover from "components/desk-link-crm-project-popover/desk-link-crm-project-popover";
import DatePicker from "features/datepicker/date-picker";
import useIsPhone from "features/files/use-is-phone";
import {
  fetchDeskCardByNumber,
  replyToDeskCard,
  addDeskNote,
  shareDeskCard,
  unshareDeskCard,
  updateDeskCard,
  mergeDeskCards,
  retryDeskMessage,
  fetchDeskProjects,
  addDeskDayPlanEntry,
  deskAttachmentUrl,
  summarizeDeskCard,
  assistDeskCompose,
} from "services/desk.service";
import {
  DeskAttachmentItem,
  DeskCardDetail,
  DeskCardShareItem,
  DeskClientWithProjects,
} from "types/Desk";
import { formatPrettyDate } from "utils/helperFunctions";
import { copyTextToClipboard } from "containers/share-board-form/share-board-link";
import { buildCardContext } from "./desk-card-context";
import DeskAttachmentChip, { isPreviewable } from "./desk-attachment-chip";
import DeskCardMoreMenu, { CardMenuAction } from "./desk-card-more-menu";
import "./desk-triage-datepicker-fill.css";

/** The composer's AI buttons: sparkles while idle, a spinner while the model works. */
function AiGlyph({ busy }: { busy: boolean }) {
  if (busy) {
    return (
      <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );
  }
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-3.5 h-3.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"
      />
    </svg>
  );
}

const PANEL_WIDE_KEY = "desk_detail_panel_wide";
/** Same cadence as the list so an open card cannot silently go stale. */
const DETAIL_REFRESH_MS = 30000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

interface Props {
  cardNumber: number;
  companyId: string;
  onClose: () => void;
  onUpdate?: () => void;
  /** Follow a card change the panel made itself, e.g. a merge. */
  onNavigate?: (cardNumber: number) => void;
}

const STATUS_COLOR: Record<string, "lime" | "amber" | "zinc"> = {
  open: "lime",
  waiting_client: "amber",
  done: "zinc",
};

/** Tomorrow, in the local YYYY-MM-DD the date pickers speak. */
function nextDayIso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toLocaleDateString("sv");
}

function splitEmails(value: string): string[] {
  return value.split(",").map((s) => s.trim()).filter(Boolean);
}

function renderMarkdownBold(text: string) {
  if (!text) return null;
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index} className="font-semibold text-zinc-900 dark:text-zinc-100">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

export default function DeskCardDetailPanel({
  cardNumber,
  companyId,
  onClose,
  onUpdate,
  onNavigate,
}: Props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const currentUserId = useSelector((state: any) => state.authReducer?.session?.id) as string | undefined;
  const isModalOpen = useSelector((state: any) => state.modalxReducer?.isOpen) as boolean | undefined;
  const [detail, setDetail] = useState<DeskCardDetail | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [preview, setPreview] = useState<DeskAttachmentItem | null>(null);
  const [previewState, setPreviewState] = useState<"loading" | "ready" | "error">("loading");
  
  const [summaryText, setSummaryText] = useState<string | null>(null);
  const [summaryAt, setSummaryAt] = useState<number | null>(null);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [projectsTree, setProjectsTree] = useState<DeskClientWithProjects[]>([]);
  const [composerTab, setComposerTab] = useState<"reply" | "note" | "share">("reply");
  const [replyTo, setReplyTo] = useState("");
  const [replyCc, setReplyCc] = useState("");
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [shareUserIds, setShareUserIds] = useState<string[]>([]);
  const [shareCandidates, setShareCandidates] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [composerText, setComposerText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);
  // Which AI button is running, and the draft it replaced (so it can be undone).
  const [aiMode, setAiMode] = useState<"fix" | "rewrite" | null>(null);
  const [aiUndoText, setAiUndoText] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isActing, setIsActing] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [mergeTarget, setMergeTarget] = useState("");
  const [mergePreview, setMergePreview] = useState<{ number: number; title: string } | null>(null);
  const [isMerging, setIsMerging] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);
  const [draftFailed, setDraftFailed] = useState(false);
  const [addedToPlan, setAddedToPlan] = useState(false);
  const [planDate, setPlanDate] = useState(() => new Date().toLocaleDateString("sv"));
  const [snoozeDate, setSnoozeDate] = useState(nextDayIso);
  const [expandedDetails, setExpandedDetails] = useState<Record<string, boolean>>({});
  // Phones only: the card fields under the status start folded, so the first message is on the first screen.
  const [showCardFields, setShowCardFields] = useState(false);
  const isCardFieldsFoldedOnPhone = !showCardFields;
  const [shares, setShares] = useState<DeskCardShareItem[]>([]);
  const [panelWide, setPanelWide] = useState(() => {
    try {
      return localStorage.getItem(PANEL_WIDE_KEY) === "1";
    } catch {
      return false;
    }
  });
  // Merge moves the conversation elsewhere — the panel follows it instead of
  // dumping the user back into the list.
  const [viewNumber, setViewNumber] = useState(cardNumber);

  const loadSeq = useRef(0);
  const summarizeSeq = useRef(0);
  const aiSeq = useRef(0);
  const composerTextRef = useRef("");
  const savedDraftRef = useRef<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const errText = useCallback(
    (e: any) => e?.response?.data?.message || e?.message || t("desk_action_failed"),
    [t]
  );

  /**
   * The model call takes seconds, during which the user may keep typing, and
   * the handler has to know what is in the textarea *now* before it overwrites
   * it. A ref synced from an effect lags a commit behind, so every write to the
   * composer goes through here and updates both.
   */
  const applyComposerText = useCallback((next: string) => {
    composerTextRef.current = next;
    setComposerText(next);
  }, []);

  const isMailboxOwner = useMemo(() => {
    if (!detail || !currentUserId) return true;
    if (!detail.mailboxOwnerUserId) return true;
    return detail.mailboxOwnerUserId === currentUserId;
  }, [detail, currentUserId]);

  const excludedShareIds = useMemo(() => {
    const ids = new Set<string>();
    if (currentUserId) ids.add(currentUserId);
    shares.forEach((s) => ids.add(s.userId));
    return Array.from(ids);
  }, [currentUserId, shares]);

  const togglePanelWide = () => {
    setPanelWide((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PANEL_WIDE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  useEffect(() => {
    setViewNumber(cardNumber);
  }, [cardNumber]);

  const loadDetail = useCallback(async () => {
    const seq = ++loadSeq.current;
    setIsDetailLoading(true);
    setLoadError(null);
    try {
      const data = await fetchDeskCardByNumber(companyId, viewNumber);
      if (seq !== loadSeq.current) return;
      setDetail(data);
      setShares(data.shares || []);
      savedDraftRef.current = data.card.draftPayload || null;
    } catch (e: any) {
      if (seq !== loadSeq.current) return;
      setDetail(null);
      setShares([]);
      setLoadError(errText(e));
    } finally {
      if (seq === loadSeq.current) setIsDetailLoading(false);
    }
  }, [companyId, viewNumber, errText]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  // Follow the card's summary rather than snapshotting it once per card: the
  // backend regenerates it on every client reply, so a re-read (⟳, a reopened
  // panel, the list refreshing) can legitimately bring a newer text — while the
  // deps only change when it actually did, so a summary this panel just
  // generated is never overwritten by the older copy still sitting in `detail`.
  // The card id is in there as well: two cards can share the same (empty)
  // summary fields, and without it the panel would carry one card's summary
  // over to the next and lock its button as already summarized.
  useEffect(() => {
    setSummaryText(detail?.card.aiSummary || null);
    setSummaryAt(detail?.card.aiSummaryAt || null);
    setFeedback(detail?.card.aiSummaryFeedback || null);
  }, [
    detail?.card.id,
    detail?.card.aiSummary,
    detail?.card.aiSummaryAt,
    detail?.card.aiSummaryFeedback,
  ]);

  useEffect(() => {
    if (!isMailboxOwner && composerTab === "reply") {
      setComposerTab("share");
    }
  }, [isMailboxOwner, composerTab]);

  useEffect(() => {
    fetchDeskProjects(companyId)
      .then((data) => setProjectsTree(data.clients || []))
      .catch(() => setProjectsTree([]));
  }, [companyId]);

  useEffect(() => {
    if (!detail) return;
    if (detail.card.draftPayload) {
      try {
        const parsed = JSON.parse(detail.card.draftPayload);
        const tab = parsed.tab === "forward" ? "share" : parsed.tab;
        if (tab === "reply" || tab === "note" || tab === "share") setComposerTab(tab);
        if (parsed.text) applyComposerText(parsed.text);
        if (parsed.to !== undefined) setReplyTo(parsed.to);
        if (parsed.cc !== undefined) setReplyCc(parsed.cc);
        // A half-written share is useless without the colleagues it was meant for.
        if (Array.isArray(parsed.userIds)) setShareUserIds(parsed.userIds);
      } catch (e) {
        // ignore
      }
    } else {
      // Reply to whoever wrote last — on a long thread the first requester is
      // often no longer the person waiting for an answer.
      const lastInbound = [...detail.timeline].reverse().find((m) => m.type === "inbound" && m.fromEmail);
      const requester = detail.participants.find((p) => p.role === "requester");
      const primary = (lastInbound?.fromEmail || requester?.email || "").toLowerCase();
      const ccs = detail.participants.filter((p) => p.email.toLowerCase() !== primary);
      setReplyTo(primary);
      setReplyCc(ccs.map((p) => p.email).join(", "));
      setComposerTab("reply");
      applyComposerText("");
      setShareUserIds([]);
    }
    setComposerError(null);
    setActionError(null);
    setRecipientError(null);
    setMergeTarget("");
    setMergePreview(null);
    setDraftSavedAt(null);
    setDraftFailed(false);
    setAddedToPlan(false);
    
    // AI Summary state initialization (the text itself is seeded below, from
    // the card, so a summary the backend regenerated is picked up too).
    summarizeSeq.current += 1;
    setIsSummarizing(false);
    aiSeq.current += 1;
    setAiMode(null);
    setAiUndoText(null);

    // The panel is reused across cards without remounting, so the pickers have
    // to be re-seeded too — otherwise the previous card's snooze date is still
    // in the field and Snooze silently applies the wrong day.
    setSnoozeDate(
      detail?.card.snoozedUntil
        ? new Date(detail.card.snoozedUntil).toLocaleDateString("sv")
        : nextDayIso()
    );
    setPlanDate(new Date().toLocaleDateString("sv"));
    // Only the identity of the open card resets the composer — depending on
    // `detail` itself would wipe what the user is typing on every poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.card.id]);

  useEffect(() => {
    if (!detail) return;
    // Prefilling To/Cc from participants must not count as a draft.
    const isBlank = !composerText.trim();
    const payload = isBlank ? null : JSON.stringify({
      tab: composerTab,
      text: composerText,
      to: replyTo,
      cc: replyCc,
      userIds: shareUserIds,
    });
    // Compare against what we last stored, not against the card we loaded —
    // otherwise every keystroke re-sends the same body and floods the audit log.
    if (payload === savedDraftRef.current) return;
    const timer = setTimeout(() => {
      updateDeskCard(companyId, detail.card.number, { draftPayload: payload })
        .then(() => {
          savedDraftRef.current = payload;
          setDraftFailed(false);
          setDraftSavedAt(payload ? Date.now() : null);
        })
        .catch(() => setDraftFailed(true));
    }, 1000);
    return () => clearTimeout(timer);
    // The autosave keys off the card number, not the whole `detail` object: a
    // background refresh replaces `detail` and would restart the debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composerTab, composerText, replyTo, replyCc, shareUserIds, detail?.card.number, companyId]);

  const refreshDetail = useCallback(
    async (notifyParent = true) => {
      if (!detail) return;
      // Taking the sequence also invalidates an in-flight loadDetail, which then
      // skips its own `finally` — so this call owns clearing the spinner.
      const seq = ++loadSeq.current;
      try {
        const data = await fetchDeskCardByNumber(companyId, detail.card.number);
        if (seq !== loadSeq.current) return;
        setDetail(data);
        setShares(data.shares || []);
        savedDraftRef.current = data.card.draftPayload || null;
      } catch (e) {
        /* panel keeps the stale copy */
      }
      if (seq === loadSeq.current) setIsDetailLoading(false);
      if (notifyParent && onUpdate) onUpdate();
    },
    [companyId, detail, onUpdate]
  );

  // The list polls; an open card must not be the one stale surface in the app.
  // The background tick does NOT notify the parent: the list runs its own poll,
  // and calling it from here fetched the whole list a second time every 30s.
  useEffect(() => {
    if (isSending || isActing || isMerging) return;
    const timer = setInterval(() => refreshDetail(false), DETAIL_REFRESH_MS);
    return () => clearInterval(timer);
  }, [refreshDetail, isSending, isActing, isMerging]);

  const act = useCallback(
    async (updates: Record<string, unknown>, optimistic?: Record<string, unknown>) => {
      if (!detail) return;
      setActionError(null);
      setIsActing(true);
      // Show the new value straight away; refreshDetail replaces it with the
      // server's answer either way, so a rejected action snaps back.
      if (optimistic) {
        setDetail((prev) => (prev ? { ...prev, card: { ...prev.card, ...optimistic } } : prev));
      }
      try {
        await updateDeskCard(companyId, detail.card.number, updates);
        await refreshDetail();
      } catch (e: any) {
        const text = errText(e);
        setActionError(text);
        toast.error(text);
        await refreshDetail();
      } finally {
        setIsActing(false);
      }
    },
    [companyId, detail, refreshDetail, errText]
  );

  const clearDraft = useCallback(async () => {
    if (!detail) return;
    applyComposerText("");
    setAiUndoText(null);
    // Whatever the assistant is still writing belongs to the draft that just
    // left; letting it land would refill the composer after a send.
    aiSeq.current += 1;
    setAiMode(null);
    savedDraftRef.current = null;
    setDraftSavedAt(null);
    setDraftFailed(false);
    localStorage.removeItem(`desk_draft_${companyId}_${detail.card.number}`);
    await updateDeskCard(companyId, detail.card.number, { draftPayload: null }).catch(() => {});
  }, [companyId, detail, applyComposerText]);

  const submitComposer = useCallback(
    async (keepStatus: boolean) => {
      if (!detail) return;
      setIsSending(true);
      setComposerError(null);
      setRecipientError(null);
      try {
        if (composerTab === "share") {
          if (!shareUserIds.length) {
            setComposerError(t("desk_share_users_required"));
            setIsSending(false);
            return;
          }
          if (!composerText.trim()) {
            setComposerError(t("desk_share_note_required"));
            setIsSending(false);
            return;
          }
          const res = await shareDeskCard(companyId, detail.card.number, {
            userIds: shareUserIds,
            text: composerText.trim(),
          });
          setShares(res.shares || []);
          setShareUserIds([]);
          await clearDraft();
          await refreshDetail();
          toast.success(t("desk_share_done"));
          if (onUpdate) onUpdate();
          return;
        }
        if (!composerText.trim()) return;
        if (composerTab === "note") {
          await addDeskNote(companyId, detail.card.number, composerText);
          await clearDraft();
          await refreshDetail();
          toast.success(t("desk_note_saved"));
          return;
        }
        const toList = splitEmails(replyTo);
        const ccList = splitEmails(replyCc);
        if (!toList.length) {
          setRecipientError(t("desk_recipients_required"));
          setIsSending(false);
          return;
        }
        const badAddress = [...toList, ...ccList].find((a) => !EMAIL_RE.test(a));
        if (badAddress) {
          setRecipientError(t("desk_invalid_recipient", { email: badAddress }));
          setIsSending(false);
          return;
        }
        await replyToDeskCard(companyId, detail.card.number, {
          to: toList,
          cc: ccList,
          text: composerText,
          keepStatus,
        });
        await clearDraft();
        await refreshDetail();
        toast.success(t("desk_reply_sent"));
      } catch (e: any) {
        const code = e?.response?.data?.code;
        const badUserId = e?.response?.data?.userId;
        if (composerTab === "share" && badUserId) {
          // The backend rejects the whole share for one ineligible colleague —
          // name them and drop them so the next click goes through.
          const who = shareCandidates.find((u) => u.id === badUserId);
          const name = (who && (who.name || who.email)) || badUserId;
          setShareUserIds((prev) => prev.filter((id) => id !== badUserId));
          const text = t(
            code === "USER_NOT_IN_COMPANY" ? "desk_share_user_not_in_company" : "desk_share_user_no_desk",
            { name }
          );
          setComposerError(text);
          toast.error(text);
          return;
        }
        if (code === "SEND_FAILED") {
          // The outbound message is already stored and shown in the timeline;
          // sending again would mail the client a second copy — retry that row.
          await clearDraft();
          await refreshDetail();
          const text = t("desk_send_failed_use_retry");
          setComposerError(text);
          toast.error(text);
          return;
        }
        const text = errText(e);
        setComposerError(text);
        toast.error(text);
        await refreshDetail();
      } finally {
        setIsSending(false);
      }
    },
    [
      companyId,
      detail,
      composerTab,
      composerText,
      replyTo,
      replyCc,
      shareUserIds,
      shareCandidates,
      clearDraft,
      refreshDetail,
      onUpdate,
      errText,
      t,
    ]
  );

  const handleUnshare = async (userId: string) => {
    if (!detail) return;
    setActionError(null);
    try {
      const res = await unshareDeskCard(companyId, detail.card.number, { userIds: [userId] });
      setShares(res.shares || []);
      await refreshDetail();
      toast.success(t("desk_unshare_done"));
      if (onUpdate) onUpdate();
    } catch (e: any) {
      const text = errText(e);
      setActionError(text);
      toast.error(text);
    }
  };

  const handleRetry = async (messageId: string) => {
    setRetryingId(messageId);
    setActionError(null);
    try {
      await retryDeskMessage(companyId, messageId);
      toast.success(t("desk_reply_sent"));
    } catch (e: any) {
      const text = errText(e);
      setActionError(text);
      toast.error(text);
    } finally {
      setRetryingId(null);
      await refreshDetail();
    }
  };

  /** Step one of merge: name the target so a typo cannot swallow the thread. */
  const previewMerge = async () => {
    if (!detail || !/^\d+$/.test(mergeTarget)) return;
    const target = Number(mergeTarget);
    if (target === detail.card.number) {
      setActionError(t("desk_merge_same_card"));
      return;
    }
    setIsMerging(true);
    setActionError(null);
    try {
      const data = await fetchDeskCardByNumber(companyId, target);
      setMergePreview({ number: data.card.number, title: data.card.title });
    } catch (e: any) {
      const text = t("desk_merge_target_not_found", { number: target });
      setActionError(text);
      toast.error(text);
    } finally {
      setIsMerging(false);
    }
  };

  const confirmMerge = async () => {
    if (!detail || !mergePreview) return;
    setIsMerging(true);
    setActionError(null);
    try {
      const res = await mergeDeskCards(companyId, detail.card.number, mergePreview.number);
      const target = res?.targetCardNumber || mergePreview.number;
      toast.success(t("desk_merge_done", { number: target }));
      setMergePreview(null);
      setMergeTarget("");
      // Follow the conversation instead of closing onto an empty list. The
      // parent owns the ?card= param, so it has to move too — otherwise a
      // refresh reopens the source card, which no longer exists as its own
      // conversation.
      setViewNumber(target);
      if (onNavigate) onNavigate(target);
      if (onUpdate) onUpdate();
    } catch (e: any) {
      const text = errText(e);
      setActionError(text);
      toast.error(text);
    } finally {
      setIsMerging(false);
    }
  };

  const requestClose = useCallback(() => {
    // Closing must not eat what was typed in the last second before the
    // autosave timer fired.
    if (detail && composerText.trim()) {
      const payload = JSON.stringify({
        tab: composerTab,
        text: composerText,
        to: replyTo,
        cc: replyCc,
        userIds: shareUserIds,
      });
      if (payload !== savedDraftRef.current) {
        updateDeskCard(companyId, detail.card.number, { draftPayload: payload }).catch(() =>
          toast.error(t("desk_draft_not_saved"))
        );
      }
    }
    onClose();
  }, [onClose, detail, companyId, composerTab, composerText, replyTo, replyCc, shareUserIds, t]);

  const handleDeleteCard = () => {
    dispatch(setModalxComponent("DeleteDeskCardConfirm"));
    dispatch(setModalxOptions({ title: t("desk_confirm_delete_card_title") }));
    dispatch(
      setModalxComponentProps({
        companyId,
        cardNumber: detail?.card.number as number,
        cardTitle: detail?.card.title,
        // Backend permanently deletes when deletedAt is already set; dialog must match.
        isTrash: Boolean(detail?.card.deletedAt),
        onConfirm: () => {
          onClose();
          if (onUpdate) onUpdate();
        },
      })
    );
    dispatch(setIsModalxOpen(true));
  };

  const handleSummarize = useCallback(async () => {
    if (isSummarizing) return;
    const number = detail?.card.number ?? viewNumber;
    const seq = ++summarizeSeq.current;
    setIsSummarizing(true);
    // The block renders a skeleton while this runs, so there is nothing to gain
    // from clearing the text — and on failure it is all the card has left.
    try {
      const res = await summarizeDeskCard(companyId, number);
      if (seq !== summarizeSeq.current) return;
      setSummaryText(res.summary);
      setSummaryAt(res.summarizedAt || Date.now());
      // A regenerated summary is a different text — the old rating is gone
      // server-side too.
      setFeedback(null);
      // Let the parent view know something updated (optional)
      onUpdate?.();
    } catch (e: any) {
      if (seq !== summarizeSeq.current) return;
      toast.error(errText(e));
    } finally {
      if (seq === summarizeSeq.current) setIsSummarizing(false);
    }
  }, [companyId, detail, viewNumber, isSummarizing, onUpdate, errText]);

  /**
   * Hand the draft to the assistant and put the result back in the textarea.
   * The previous text is kept so one click undoes it — the assistant is a
   * suggestion, never the last word on what the user sends.
   */
  const runComposerAi = useCallback(
    async (mode: "fix" | "rewrite") => {
      const source = composerText;
      if (!source.trim() || aiMode) return;
      const number = detail?.card.number ?? viewNumber;
      const seq = ++aiSeq.current;
      setAiMode(mode);
      try {
        const res = await assistDeskCompose(companyId, number, mode, source);
        if (seq !== aiSeq.current) return;
        // The call takes seconds; whatever the user typed meanwhile wins.
        if (composerTextRef.current !== source) {
          toast.warning(t("desk_ai_draft_changed"));
          return;
        }
        if (res.text.trim() === source.trim()) {
          toast.success(t("desk_ai_no_change"));
          return;
        }
        setAiUndoText(source);
        applyComposerText(res.text);
        toast.success(t(mode === "fix" ? "desk_ai_fixed" : "desk_ai_rewritten"));
      } catch (e: any) {
        if (seq !== aiSeq.current) return;
        // The two refusals a user can act on get a translated message; the rest
        // fall back to whatever the backend said.
        const code = e?.response?.data?.code;
        if (code === "TEXT_TOO_LONG") toast.error(t("desk_ai_too_long"));
        else if (code === "AI_DISABLED") toast.error(t("desk_ai_disabled"));
        else toast.error(errText(e));
      } finally {
        if (seq === aiSeq.current) setAiMode(null);
      }
    },
    [companyId, composerText, aiMode, detail, viewNumber, t, errText, applyComposerText]
  );

  const undoComposerAi = useCallback(() => {
    if (aiUndoText === null) return;
    applyComposerText(aiUndoText);
    setAiUndoText(null);
  }, [aiUndoText, applyComposerText]);

  const handleSummaryFeedback = async (val: "up" | "down") => {
    if (feedback === val) return;
    const number = detail?.card.number ?? viewNumber;
    setFeedback(val);
    try {
      await updateDeskCard(companyId, number, { aiSummaryFeedback: val });
    } catch (e: any) {
      toast.error(errText(e));
    }
  };

  /**
   * The whole card as plain text — for pasting into a chat with someone (or
   * something) that has no access to the ERP.
   */
  const handleCopyContext = useCallback(async () => {
    if (!detail) return;
    const ok = await copyTextToClipboard(buildCardContext(detail, { companyId }));
    if (ok) toast.success(t("desk_copy_context_done"));
    else toast.error(t("desk_copy_context_failed"));
  }, [detail, companyId, t]);

  const handleRestoreCard = () => {
    dispatch(setModalxComponent("RestoreDeskCardConfirm"));
    dispatch(setModalxOptions({ title: t("desk_restore") }));
    dispatch(
      setModalxComponentProps({
        companyId,
        cardNumber: detail?.card.number as number,
        isDraft: Boolean(detail?.card.isDraft),
        onConfirm: () => {
          loadDetail();
          if (onUpdate) onUpdate();
        },
      })
    );
    dispatch(setIsModalxOpen(true));
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // A confirm dialog on top owns the keyboard: Escape there must not also
      // close the panel underneath it, and Tab must stay in the dialog.
      if (isModalOpen) return;
      if (e.key === "Tab") {
        const root = panelRef.current;
        if (!root) return;
        const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
          (el) => el.offsetParent !== null || el === document.activeElement
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        const outside = !active || !root.contains(active);
        if (e.shiftKey && (outside || active === first)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (outside || active === last)) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      if (e.key !== "Escape") return;
      if (preview) {
        setPreview(null);
        return;
      }
      // Escape inside a form control must not nuke a half-written reply —
      // it blurs the field instead (reviewer finding #3).
      const target = e.target as HTMLElement | null;
      const tag = target && target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (target && target.isContentEditable)) {
        (target as HTMLElement).blur();
        return;
      }
      requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [requestClose, preview, isModalOpen]);

  // The panel is a modal dialog: focus moves in, the page behind stops
  // scrolling, and whatever was focused before gets it back on close.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
  }, []);

  useEffect(() => {
    setPreviewState("loading");
  }, [preview]);

  // A snoozed card must open showing the date it is snoozed to, not "tomorrow".
  useEffect(() => {
    if (detail?.card.snoozedUntil) {
      setSnoozeDate(new Date(detail.card.snoozedUntil).toLocaleDateString("sv"));
    }
  }, [detail?.card.id, detail?.card.snoozedUntil]);

  const projectNames = useMemo(() => {
    const map = new Map<string, string>();
    projectsTree.forEach((client) => client.projects.forEach((p) => map.set(p.id, p.name)));
    return map;
  }, [projectsTree]);

  /** Turns a stored event row into a sentence instead of a raw enum. */
  const eventText = useCallback(
    (eventType: string | null, raw: Record<string, unknown> | null) => {
      const payload: Record<string, unknown> = { ...(raw || {}) };
      if (eventType === "status_changed") {
        if (payload.from) payload.from = t(`desk_status_${payload.from}`);
        if (payload.to) payload.to = t(`desk_status_${payload.to}`);
      }
      if (eventType === "project_moved") {
        payload.to = payload.to ? projectNames.get(String(payload.to)) || String(payload.to) : t("desk_no_project");
      }
      if (eventType === "snoozed" && payload.until) {
        payload.until = formatPrettyDate(Number(payload.until), "lt-LT", true);
      }
      if (Array.isArray(payload.names)) payload.names = payload.names.join(", ");
      if (Array.isArray(payload.to)) payload.to = payload.to.join(", ");
      return t(`desk_event_${eventType}`, { ...payload, defaultValue: eventType || "" });
    },
    [t, projectNames]
  );

  const isTrashed = Boolean(detail?.card.deletedAt);
  const isReadOnly = isActing || isSending || isMerging;

  // A summary describes the thread as it stood. Once a newer message lands the
  // backend regenerates it by itself, but that runs after ingest and can fail
  // (no API key, model down) — so a summary older than the last message is
  // labelled as such and the button becomes a manual refresh instead of a
  // permanently spent one.
  const isSummaryStale = Boolean(
    summaryText &&
      detail?.card.lastMessageAt &&
      (!summaryAt || detail.card.lastMessageAt > summaryAt)
  );
  const isSummaryCurrent = Boolean(summaryText) && !isSummaryStale;

  // On a phone fields are 16px (iOS zooms into anything smaller) and 44px tall.
  const inputCls = "rounded-lg border border-zinc-300 bg-transparent px-3 py-1.5 text-sm outline-none transition-all duration-150 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:border-zinc-700 dark:focus:ring-indigo-400/20 dark:focus:border-indigo-400 max-md:min-h-11 max-md:text-base";
  const btnCls = "rounded-lg border border-zinc-200 bg-transparent px-3 py-1.5 text-sm font-semibold hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900 transition-colors duration-150 disabled:opacity-40 max-md:min-h-11";
  const aiBtnCls =
    "inline-flex items-center gap-1.5 rounded-lg border border-fuchsia-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-zinc-700 hover:bg-fuchsia-50 disabled:opacity-40 transition-colors duration-150 dark:bg-zinc-950 dark:text-zinc-200 dark:hover:bg-fuchsia-950 max-md:min-h-11 max-md:text-xs";
  const primaryBtnCls = "rounded-lg bg-indigo-600 px-3.5 py-1.5 text-sm font-semibold text-white hover:bg-indigo-500 active:bg-indigo-700 shadow-sm transition-colors duration-150 disabled:opacity-40 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:active:bg-indigo-600 max-md:min-h-11";
  const previewBtnCls =
    "flex items-center rounded-xl bg-white/10 px-3.5 py-1.5 text-xs font-semibold hover:bg-white/20 transition-colors max-md:min-h-11 max-md:px-4 max-md:text-sm";
  // The way to read a file the preview cannot show, so it stands out on a phone.
  const previewDownloadCls = previewBtnCls + " max-md:bg-white max-md:text-zinc-900 max-md:hover:bg-zinc-200";

  // The header row has no room for these on a phone, so they sit behind «⋯»
  // (the same buttons are shown inline from 768px up).
  const moreActions: CardMenuAction[] = detail
    ? [
        { key: "copy", label: t("desk_copy_context"), onClick: handleCopyContext, disabled: isDetailLoading },
        ...(isTrashed
          ? []
          : [
              {
                key: "archive",
                label: t(detail.card.archivedAt ? "desk_unarchive" : "desk_archive_action"),
                onClick: () => act({ archived: !detail.card.archivedAt }),
                disabled: isReadOnly,
              },
            ]),
        { key: "refresh", label: t("desk_refresh_card"), onClick: loadDetail, disabled: isDetailLoading },
        {
          key: "delete",
          label: t(isTrashed ? "desk_delete_permanently" : "desk_delete_card"),
          onClick: handleDeleteCard,
          critical: true,
        },
      ]
    : [];

  return (
    <>
      <div className="fixed inset-0 z-40 bg-zinc-950/20 backdrop-blur-sm animate-fadeIn" onClick={requestClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="desk-card-panel-title"
        aria-label={`${t("desk_card")} #${viewNumber}`}
        tabIndex={-1}
        className={
          "fixed inset-y-0 right-0 z-50 w-full max-w-2xl animate-slideIn transition-[max-width] duration-200 outline-none " +
          (panelWide ? "lg:max-w-5xl" : "")
        }
      >
        {/* Outside the scroll container so overflow-y-auto cannot clip the half-out button.
            Only where there is free room left of the panel: below 1024px it is full width or 672px of a tablet. */}
        <button
          type="button"
          onClick={togglePanelWide}
          className="absolute left-0 top-20 z-[60] hidden -translate-x-1/2 h-9 w-9 items-center justify-center lg:flex rounded-full border border-zinc-200 bg-white text-zinc-600 shadow-md hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 font-mono text-sm"
          title={t(panelWide ? "desk_panel_narrow" : "desk_panel_widen")}
          aria-label={t(panelWide ? "desk_panel_narrow" : "desk_panel_widen")}
        >
          {panelWide ? "→" : "←"}
        </button>
        <div className="h-full overflow-y-auto border-l border-zinc-200 bg-white p-3 shadow-2xl sm:p-6 dark:border-zinc-800 dark:bg-zinc-900/95 backdrop-blur-md">
        {isDetailLoading && (
          <div className="space-y-6 animate-pulse">
            {/* Header Skeleton */}
            <div className="border-b border-zinc-100 pb-4 dark:border-zinc-800 space-y-2">
              <div className="h-3 w-16 bg-zinc-200 dark:bg-zinc-800 rounded-md" />
              <div className="h-6 w-3/4 bg-zinc-200 dark:bg-zinc-800 rounded-md" />
              <div className="flex gap-1.5 pt-1">
                <div className="h-4 w-12 bg-zinc-200 dark:bg-zinc-800 rounded" />
                <div className="h-4 w-20 bg-zinc-200 dark:bg-zinc-800 rounded" />
                <div className="h-4 w-16 bg-zinc-200 dark:bg-zinc-800 rounded" />
              </div>
            </div>

            {/* Triage Bar Skeleton */}
            <div className="rounded-2xl bg-zinc-50 p-4 dark:bg-zinc-950/60 border border-zinc-100 dark:border-zinc-800 space-y-3">
              <div className="flex flex-wrap gap-3">
                <div className="h-8 w-24 bg-zinc-200 dark:bg-zinc-800 rounded-lg" />
                <div className="h-8 w-32 bg-zinc-200 dark:bg-zinc-800 rounded-lg" />
                <div className="h-8 w-28 bg-zinc-200 dark:bg-zinc-800 rounded-lg" />
              </div>
              <div className="h-8 w-full bg-zinc-200 dark:bg-zinc-800 rounded-lg pt-1" />
            </div>

            {/* Timeline Messages Skeleton */}
            <div className="space-y-4 pt-2">
              <div className="sm:mr-8 rounded-2xl border border-zinc-200/80 bg-zinc-50/50 p-3 sm:p-4 dark:border-zinc-800 shadow-sm space-y-2">
                <div className="flex justify-between">
                  <div className="h-3.5 w-24 bg-zinc-200 dark:bg-zinc-800 rounded-md" />
                  <div className="h-3.5 w-16 bg-zinc-200 dark:bg-zinc-800 rounded-md" />
                </div>
                <div className="h-3.5 w-full bg-zinc-200 dark:bg-zinc-800 rounded-md" />
                <div className="h-3.5 w-5/6 bg-zinc-200 dark:bg-zinc-800 rounded-md" />
              </div>
              <div className="sm:ml-8 rounded-2xl border border-emerald-300/40 bg-emerald-500/5 p-3 sm:p-4 shadow-sm space-y-2">
                <div className="flex justify-between">
                  <div className="h-3.5 w-20 bg-zinc-200/70 dark:bg-zinc-800/70 rounded-md" />
                  <div className="h-3.5 w-16 bg-zinc-200/70 dark:bg-zinc-800/70 rounded-md" />
                </div>
                <div className="h-3.5 w-11/12 bg-zinc-200/70 dark:bg-zinc-800/70 rounded-md" />
                <div className="h-3.5 w-4/5 bg-zinc-200/70 dark:bg-zinc-800/70 rounded-md" />
              </div>
            </div>

            {/* Composer Skeleton */}
            <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 overflow-hidden bg-white dark:bg-zinc-950 shadow-md p-4 space-y-3">
              <div className="h-8 w-40 bg-zinc-200 dark:bg-zinc-800 rounded-xl" />
              <div className="h-20 w-full bg-zinc-100 dark:bg-zinc-900 rounded-xl" />
              <div className="h-8 w-24 bg-zinc-200 dark:bg-zinc-800 rounded-xl" />
            </div>
          </div>
        )}
        {!isDetailLoading && !detail && loadError && (
          <div className="flex items-start justify-between gap-4 border-b border-zinc-100 pb-4 dark:border-zinc-800">
            <div className="flex-1 space-y-2.5 rounded-xl bg-red-500/10 p-3.5 border border-red-500/20">
              <Text className="!text-zinc-500 font-mono text-xs">
                {t("desk_card")} #{viewNumber}
              </Text>
              <Text className="!text-red-700 dark:!text-red-400 font-medium">{loadError}</Text>
              <button type="button" onClick={loadDetail} className={btnCls}>
                {t("retry")}
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="h-8 w-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all shrink-0 max-md:h-11 max-md:w-11"
              aria-label={t("close")}
            >
              ✕
            </button>
          </div>
        )}
        {detail && (
          <div className="space-y-6">
            {/* Title and ✕ share the first row; the actions get their own row below
                (and wrap) — all of them in one line were wider than the panel. */}
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-3 border-b border-zinc-100 pb-4 dark:border-zinc-800">
              <div>
                <Text className="!text-zinc-400 font-mono text-xs">#{detail.card.number}</Text>
                <Heading level={2} id="desk-card-panel-title" className="!font-bold mt-1 break-words text-xl">{detail.card.title}</Heading>
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  <Badge color={STATUS_COLOR[detail.card.status] || "zinc"} className="px-1.5 py-0.5 rounded text-[10px] font-semibold">
                    {t(`desk_status_${detail.card.status}`)}
                  </Badge>
                  {detail.card.client && (
                    <Badge color="blue" className="px-1.5 py-0.5 rounded text-[10px] font-semibold">{detail.card.client.name}</Badge>
                  )}
                  {detail.card.project && (
                    <Badge color="purple" className="px-1.5 py-0.5 rounded text-[10px] font-semibold">{detail.card.project.name}</Badge>
                  )}
                  {detail.card.crmProject && (
                    <Badge color="lime" className="px-1.5 py-0.5 rounded text-[10px] font-semibold">
                      {detail.card.crmProject.name}
                    </Badge>
                  )}
                  {detail.card.isNoise && <Badge color="zinc" className="px-1.5 py-0.5 rounded text-[10px] font-semibold">{t("desk_noise")}</Badge>}
                  {detail.card.isManual && <Badge color="indigo" className="px-1.5 py-0.5 rounded text-[10px] font-semibold">{t("desk_badge_manual")}</Badge>}
                  {detail.card.isDraft && <Badge color="amber" className="px-1.5 py-0.5 rounded text-[10px] font-semibold">{t("desk_draft")}</Badge>}
                  {detail.card.isSharedWithMe ? (
                    <Badge color="violet" className="px-1.5 py-0.5 rounded text-[10px] font-semibold">{t("desk_shared_with_me")}</Badge>
                  ) : detail.card.isShared ? (
                    <Badge color="violet" className="px-1.5 py-0.5 rounded text-[10px] font-semibold">{t("desk_shared")}</Badge>
                  ) : null}
                  {isTrashed && (
                    <Badge color="red" className="px-1.5 py-0.5 rounded text-[10px] font-semibold">{t("desk_deleted")}</Badge>
                  )}
                  {detail.card.snoozedUntil && detail.card.snoozedUntil > Date.now() && (
                    <Badge color="amber" className="px-1.5 py-0.5 rounded text-[10px] font-semibold">
                      {t("desk_snoozed_until", { date: formatPrettyDate(detail.card.snoozedUntil, "lt-LT", false) })}
                    </Badge>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={requestClose}
                className="h-8 w-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all max-md:h-11 max-md:w-11"
                aria-label={t("close")}
              >
                ✕
              </button>
              <div className="col-span-2 flex flex-wrap items-center gap-1.5">
                {!isReadOnly && (
                  <DeskLinkCrmProjectPopover
                    companyId={companyId}
                    linkedProject={detail.card.crmProject || null}
                    disabled={isReadOnly}
                    onLink={async (projectId) => {
                      await act({ crmProject: projectId });
                      toast.success(
                        t("desk_crm_project_linked", {
                          defaultValue: "Deal linked.",
                        })
                      );
                      if (onUpdate) onUpdate();
                    }}
                    onUnlink={async () => {
                      await act({ crmProject: null }, { crmProject: null });
                      toast.success(
                        t("desk_crm_project_unlinked", {
                          defaultValue: "Deal unlinked.",
                        })
                      );
                      if (onUpdate) onUpdate();
                    }}
                  />
                )}
                <button
                  type="button"
                  disabled={isDetailLoading}
                  onClick={handleCopyContext}
                  title={t("desk_copy_context_hint")}
                  className="h-8 px-3 flex items-center justify-center gap-1.5 rounded-lg text-xs font-semibold bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 transition-colors dark:bg-zinc-950 dark:border-zinc-800 dark:text-zinc-200 max-md:hidden"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                    />
                  </svg>
                  {t("desk_copy_context")}
                </button>
                <button
                  type="button"
                  disabled={isDetailLoading || isSummarizing || isSummaryCurrent}
                  onClick={handleSummarize}
                  className={`h-8 px-3 flex items-center justify-center gap-1.5 rounded-lg text-xs font-semibold bg-white border max-md:h-11 max-md:text-sm ${isSummaryCurrent ? 'border-zinc-200 text-zinc-500 cursor-default' : 'border-blue-400/50 hover:bg-zinc-50 text-zinc-700'}`}
                  style={!isSummaryCurrent ? {
                    backgroundImage: 'linear-gradient(white, white), linear-gradient(to right, #e879f9, #38bdf8)',
                    backgroundOrigin: 'border-box',
                    backgroundClip: 'padding-box, border-box',
                    border: '1px solid transparent'
                  } : {}}
                >
                  {isSummarizing ? (
                    <svg className="animate-spin h-4 w-4 text-zinc-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : isSummaryCurrent ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-zinc-400">
                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                      </svg>
                      {t("desk_summarized")}
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                      </svg>
                      {t(isSummaryStale ? "desk_summarize_refresh" : "desk_summarize")}
                    </>
                  )}
                </button>
                {detail.card.isDraft && (
                  <button
                    type="button"
                    disabled={isReadOnly}
                    onClick={() => act({ isDraft: false }, { isDraft: false })}
                    className="h-8 px-2.5 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white transition-all disabled:opacity-40 max-md:h-11 max-md:px-3.5 max-md:text-sm"
                  >
                    {t("desk_publish")}
                  </button>
                )}
                {isTrashed ? (
                  <button
                    type="button"
                    onClick={handleRestoreCard}
                    className="h-8 px-2.5 rounded-lg text-xs font-semibold text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 transition-all max-md:h-11 max-md:px-3.5 max-md:text-sm"
                  >
                    {t("desk_restore")}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={isReadOnly}
                    onClick={() => act({ archived: !detail.card.archivedAt })}
                    className="h-8 px-2.5 rounded-lg text-xs font-semibold text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800 transition-all disabled:opacity-40 max-md:hidden"
                  >
                    {t(detail.card.archivedAt ? "desk_unarchive" : "desk_archive_action")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleDeleteCard}
                  className="h-8 px-2.5 rounded-lg text-xs font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all max-md:hidden"
                >
                  {t(isTrashed ? "desk_delete_permanently" : "desk_delete_card")}
                </button>
                <button
                  type="button"
                  disabled={isDetailLoading}
                  onClick={loadDetail}
                  className="h-8 w-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all disabled:opacity-40 max-md:hidden"
                  title={t("desk_refresh_card")}
                  aria-label={t("desk_refresh_card")}
                >
                  ⟳
                </button>
                <DeskCardMoreMenu actions={moreActions} label={t("more")} />
              </div>
            </div>

            {/* Action failures belong next to the controls that caused them, not
                hundreds of pixels below the composer. */}
            {actionError && (
              <div className="flex items-start justify-between gap-3 rounded-xl bg-red-500/10 p-3 text-xs text-red-700 border border-red-500/20 dark:text-red-400">
                <span className="font-medium">{actionError}</span>
                <button
                  type="button"
                  onClick={() => setActionError(null)}
                  className="shrink-0 font-bold hover:opacity-70"
                  aria-label={t("close")}
                >
                  ✕
                </button>
              </div>
            )}

            {/* AI Summary Block */}
            {(isSummarizing || summaryText) && (
              <div className="rounded-2xl bg-zinc-100 dark:bg-zinc-800/40 p-5 mt-4 mb-2 text-sm leading-relaxed relative text-zinc-800 dark:text-zinc-200">
                {isSummarizing ? (
                  <div className="space-y-3 animate-pulse">
                    <div className="h-4 bg-gradient-to-r from-purple-200/50 to-blue-200/50 dark:from-purple-900/30 dark:to-blue-900/30 rounded w-full"></div>
                    <div className="h-4 bg-gradient-to-r from-purple-200/50 to-blue-200/50 dark:from-purple-900/30 dark:to-blue-900/30 rounded w-5/6"></div>
                    <div className="h-4 bg-gradient-to-r from-purple-200/50 to-blue-200/50 dark:from-purple-900/30 dark:to-blue-900/30 rounded w-1/3"></div>
                  </div>
                ) : (
                  <>
                    <div className="whitespace-pre-wrap text-zinc-700 dark:text-zinc-300">
                      {renderMarkdownBold(summaryText || "")}
                    </div>
                    {isSummaryStale && (
                      <div className="mt-3 text-xs text-amber-600 dark:text-amber-400">
                        {t("desk_ai_summary_stale")}
                      </div>
                    )}
                    <div className="mt-4 pt-3 border-t border-zinc-200 dark:border-zinc-700/50 flex justify-between items-center text-xs text-zinc-500">
                      <span>
                        {t("desk_ai_summary_disclaimer")}
                        {summaryAt
                          ? ` · ${t("desk_ai_summary_generated_at", {
                              date: formatPrettyDate(summaryAt, "lt-LT", true),
                            })}`
                          : ""}
                      </span>
                      <div className="flex gap-2">
                        <button 
                          type="button" 
                          onClick={() => handleSummaryFeedback("up")}
                          className={`hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors ${feedback === "up" ? "text-emerald-500 dark:text-emerald-400" : ""}`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                          </svg>
                        </button>
                        <button 
                          type="button" 
                          onClick={() => handleSummaryFeedback("down")}
                          className={`hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors ${feedback === "down" ? "text-red-500 dark:text-red-400" : ""}`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Triage bar */}
            <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-zinc-50 p-4 text-xs dark:bg-zinc-950/60 border border-zinc-100 dark:border-zinc-800">
              <div className="flex flex-col gap-1.5 max-md:w-full">
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">{t("status")}</span>
                <select
                  value={detail.card.status}
                  disabled={isReadOnly}
                  aria-label={t("status")}
                  onChange={(e) => act({ status: e.target.value }, { status: e.target.value })}
                  className={inputCls + " py-1 px-2.5 disabled:opacity-50 max-md:w-full"}
                >
                  <option value="open">{t("desk_status_open")}</option>
                  <option value="waiting_client">{t("desk_status_waiting_client")}</option>
                  <option value="done">{t("desk_status_done")}</option>
                </select>
              </div>
              <button
                type="button"
                aria-expanded={showCardFields}
                onClick={() => setShowCardFields((prev) => !prev)}
                className="flex min-h-11 w-full items-center justify-between gap-2 rounded-lg border border-zinc-200 px-3 text-left text-sm font-semibold text-zinc-600 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-900 md:hidden"
              >
                {t("desk_card_fields_toggle")}
                <span aria-hidden="true">{showCardFields ? "▲" : "▼"}</span>
              </button>
              <div className={clsx("flex flex-col gap-1.5 max-md:w-full", isCardFieldsFoldedOnPhone && "max-md:hidden")}>
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">{t("desk_project")}</span>
                <select
                  value={detail.card.project?.id || ""}
                  disabled={isReadOnly}
                  aria-label={t("desk_project")}
                  onChange={(e) =>
                    act(
                      { project: e.target.value || null },
                      {
                        project: e.target.value
                          ? { id: e.target.value, name: projectNames.get(e.target.value) || "" }
                          : null,
                      }
                    )
                  }
                  className={inputCls + " py-1 px-2.5 max-w-[180px] disabled:opacity-50 max-md:w-full max-md:max-w-none"}
                >
                  <option value="">{t("desk_no_project")}</option>
                  {projectsTree.map((client) => (
                    <optgroup key={client.id} label={client.name}>
                      {client.projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className={clsx("flex flex-wrap items-end gap-4 h-full self-end max-md:w-full max-md:flex-col max-md:items-stretch max-md:gap-3", isCardFieldsFoldedOnPhone && "max-md:hidden")}>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">{t("desk_snooze_until")}</span>
                  <div className="flex items-center gap-2">
                    <div className="desk-triage-datepicker desk-triage-datepicker-fill">
                      <DatePicker
                        selected={snoozeDate}
                        title={t("desk_snooze_until")}
                        fullWidth={false}
                        disableDatesBefore={new Date(new Date().setHours(0, 0, 0, 0)) as any}
                        disableDatesAfter={false}
                        onChange={(d: Date | null) => {
                          if (d) setSnoozeDate(d.toISOString().split("T")[0]);
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      disabled={!snoozeDate || isReadOnly}
                      onClick={() => {
                        if (!snoozeDate) return;
                        const [y, m, d] = snoozeDate.split("-").map(Number);
                        const until = new Date(y, m - 1, d, 9, 0, 0, 0);
                        act({ snoozedUntil: until.getTime() }, { snoozedUntil: until.getTime() });
                      }}
                      className={btnCls + " py-1 px-2.5"}
                    >
                      {t("desk_snooze")}
                    </button>
                    {detail.card.snoozedUntil && (
                      <button
                        type="button"
                        disabled={isReadOnly}
                        onClick={() => act({ snoozedUntil: null }, { snoozedUntil: null })}
                        className={btnCls + " py-1 px-2.5"}
                      >
                        {t("desk_unsnooze")}
                      </button>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={isReadOnly}
                  onClick={() => act({ isNoise: !detail.card.isNoise }, { isNoise: !detail.card.isNoise })}
                  className={btnCls + " py-1 px-2.5 self-end mb-[1px] max-md:self-stretch max-md:mb-0"}
                >
                  {detail.card.isNoise ? t("desk_unmark_noise") : t("desk_mark_noise")}
                </button>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">{t("desk_plan_on")}</span>
                  <div className="flex items-center gap-2">
                    <div className="desk-triage-datepicker desk-triage-datepicker-fill">
                      <DatePicker
                        selected={planDate}
                        title={t("desk_plan_on")}
                        fullWidth={false}
                        disableDatesAfter={false}
                        onChange={(d: Date | null) => {
                          if (d) setPlanDate(d.toISOString().split("T")[0]);
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      disabled={addedToPlan || !planDate || isReadOnly}
                      onClick={async () => {
                        if (!planDate) return;
                        setIsActing(true);
                        setActionError(null);
                        try {
                          await addDeskDayPlanEntry(companyId, planDate, {
                            cardNumber: detail.card.number,
                          });
                          setAddedToPlan(true);
                          setTimeout(() => setAddedToPlan(false), 2500);
                          toast.success(t("desk_planned"));
                          if (onUpdate) onUpdate();
                        } catch (e: any) {
                          const text = errText(e);
                          setActionError(text);
                          toast.error(text);
                        } finally {
                          setIsActing(false);
                        }
                      }}
                      className={primaryBtnCls + " py-1 px-2.5 disabled:opacity-60"}
                    >
                      {addedToPlan ? "✓ " + t("desk_planned") : t("desk_add_to_plan")}
                    </button>
                  </div>
                </div>
              </div>
              <div className={clsx("flex flex-col gap-1.5 w-full border-t border-zinc-100 dark:border-zinc-900 pt-3 mt-1.5", isCardFieldsFoldedOnPhone && "max-md:hidden")}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">{t("desk_points")}</span>
                  <span className="flex items-center gap-1">
                    {[1, 2, 3, 5, 8].map((n) => (
                      <button
                        key={n}
                        type="button"
                        disabled={isReadOnly}
                        aria-pressed={detail.card.estimatePoints === n}
                        onClick={() =>
                          act(
                            { estimatePoints: detail.card.estimatePoints === n ? null : n },
                            { estimatePoints: detail.card.estimatePoints === n ? null : n }
                          )
                        }
                        className={
                          "h-7 w-7 max-md:h-11 max-md:w-11 rounded-lg text-xs font-semibold flex items-center justify-center transition-all " +
                          (detail.card.estimatePoints === n
                            ? "bg-indigo-600 text-white shadow-sm dark:bg-indigo-500"
                            : "bg-zinc-200/50 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700")
                        }
                      >
                        {n}
                      </button>
                    ))}
                  </span>
                </div>
              </div>
              <div className={clsx("flex flex-col gap-1.5 w-full border-t border-zinc-100 dark:border-zinc-900 pt-3", isCardFieldsFoldedOnPhone && "max-md:hidden")}>
                <div className="flex items-center justify-between gap-2 max-md:flex-wrap">
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold">{t("desk_merge_conversation")}</span>
                  <div className="flex items-center gap-1.5 max-md:w-full">
                    <input
                      value={mergeTarget}
                      onChange={(e) => {
                        setMergeTarget(e.target.value);
                        setMergePreview(null);
                      }}
                      placeholder={t("desk_merge_into_number")}
                      aria-label={t("desk_merge_conversation")}
                      className={inputCls + " w-20 py-1 px-2 text-xs max-md:min-w-0 max-md:flex-1"}
                    />
                    <button
                      type="button"
                      disabled={!/^\d+$/.test(mergeTarget) || isMerging}
                      onClick={previewMerge}
                      className={btnCls + " py-1 px-2.5"}
                    >
                      {t("desk_merge")}
                    </button>
                  </div>
                </div>
                {/* Merging is one-way — name the target card before doing it. */}
                {mergePreview && (
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-[11px] text-amber-900 dark:text-amber-300">
                    <span className="font-medium">
                      {t("desk_merge_confirm", {
                        source: detail.card.number,
                        target: mergePreview.number,
                        title: mergePreview.title,
                      })}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={isMerging}
                        onClick={confirmMerge}
                        className="rounded-lg bg-amber-500 px-2.5 py-1 font-bold text-amber-950 hover:bg-amber-400 disabled:opacity-40 transition-colors max-md:min-h-11"
                      >
                        {isMerging ? t("desk_sending") : t("desk_merge")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setMergePreview(null)}
                        className={btnCls + " py-1 px-2.5 text-[11px]"}
                      >
                        {t("desk_confirm_delete_card_no")}
                      </button>
                    </span>
                  </div>
                )}
              </div>
            </div>

            {detail.participants.length > 0 && (
              <div>
                <Text className="!text-[10px] !uppercase !tracking-wide !text-zinc-400 !font-semibold">
                  {t("desk_participants")}
                </Text>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {detail.participants.map((p) => (
                    <Badge key={p.id} color={p.role === "requester" ? "blue" : "zinc"} className="px-2 py-0.5 rounded-md text-xs font-semibold">
                      {p.name ? `${p.name} <${p.email}>` : p.email}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {shares.length > 0 && (
              <div>
                <Text className="!text-[10px] !uppercase !tracking-wide !text-zinc-400 !font-semibold">
                  {t("desk_shared_with")}
                </Text>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {shares.map((s) => {
                    const canRemove =
                      isMailboxOwner || (currentUserId != null && s.sharedBy === currentUserId);
                    return (
                      <span
                        key={s.userId}
                        className="inline-flex items-center gap-1 rounded-md bg-violet-500/15 text-violet-700 dark:text-violet-400 px-2 py-0.5 text-xs font-semibold"
                      >
                        {s.name || s.email || s.userId}
                        {canRemove && (
                          <button
                            type="button"
                            onClick={() => handleUnshare(s.userId)}
                            className="ml-0.5 text-violet-500 hover:text-violet-800 dark:hover:text-violet-200"
                            title={t("desk_unshare")}
                            aria-label={t("desk_unshare")}
                          >
                            ×
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-4 pt-2">
              {detail.timeline.map((m) => {
                const authorName = (m as any).authorName as string | undefined;
                return (
                <div
                  key={m.id}
                  className={
                    m.type === "event"
                      ? "text-center text-[10px] text-zinc-400 font-mono py-1"
                      : m.type === "note"
                        ? "rounded-2xl border border-yellow-300/40 bg-yellow-500/5 p-3 shadow-sm sm:p-4"
                        : m.type === "outbound"
                          ? "rounded-2xl border border-emerald-300/40 bg-emerald-500/5 p-3 shadow-sm sm:ml-8 sm:p-4"
                          : "rounded-2xl border border-zinc-200/80 bg-zinc-50/50 p-3 dark:border-zinc-800 shadow-sm sm:mr-8 sm:p-4"
                  }
                >
                  {m.type === "event" ? (
                    <span>
                      {eventText(m.eventType, m.eventPayload)}
                      {authorName ? ` · ${authorName}` : ""} · {formatPrettyDate(m.createdAt, "lt-LT", true)}
                    </span>
                  ) : (
                    <div>
                      <div className="flex flex-col gap-1 text-[11px] border-b border-zinc-100 dark:border-zinc-900 pb-2 mb-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-zinc-700 dark:text-zinc-300">
                              {m.type === "outbound"
                                ? authorName || m.fromEmail || t("desk_you")
                                : m.type === "note"
                                  ? authorName || t("desk_note")
                                  : m.fromName || m.fromEmail || t("desk_note")}
                            </span>
                            {m.type === "note" && (
                              <span className="text-[10px] text-zinc-500">· {t("desk_note")}</span>
                            )}
                            {m.type !== "note" && (
                              <button
                                type="button"
                                aria-expanded={Boolean(expandedDetails[m.id])}
                                onClick={() => {
                                  setExpandedDetails(prev => ({ ...prev, [m.id]: !prev[m.id] }));
                                }}
                                className="text-zinc-500 hover:text-indigo-600 transition-colors text-[9px] px-1.5 py-0.5 rounded bg-zinc-100/50 hover:bg-zinc-100 dark:bg-zinc-800/50 dark:hover:bg-zinc-800 flex items-center gap-0.5 font-bold max-md:min-h-11 max-md:px-3 max-md:text-xs [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:px-3 [@media(pointer:coarse)]:text-xs"
                              >
                                {t("details")} {expandedDetails[m.id] ? "▲" : "▼"}
                              </button>
                            )}
                            {m.isAuto && <span className="text-[10px] text-zinc-400">· {t("desk_auto_mail")}</span>}
                          </div>
                          <span className="whitespace-nowrap font-mono text-[10px] text-zinc-400">{formatPrettyDate(m.createdAt, "lt-LT", true)}</span>
                        </div>

                        {/* Expanded details */}
                        {expandedDetails[m.id] && (
                          <div className="mt-2 p-3 rounded-xl border border-zinc-200/80 bg-white dark:border-zinc-800 dark:bg-zinc-950 text-xs space-y-1.5 text-zinc-600 dark:text-zinc-400 leading-relaxed font-semibold animate-fadeIn">
                            <div>
                              <span className="font-bold text-zinc-500 inline-block w-12">{t("desk_from")}:</span>
                              <span className="font-mono text-zinc-700 dark:text-zinc-300">{m.fromName ? `${m.fromName} <${m.fromEmail}>` : m.fromEmail}</span>
                            </div>
                            {m.toEmails && m.toEmails.length > 0 && (
                              <div>
                                <span className="font-bold text-zinc-500 inline-block w-12">{t("desk_to")}:</span>
                                <span className="font-mono text-zinc-700 dark:text-zinc-300">{m.toEmails.map(addr => addr.name ? `${addr.name} <${addr.email}>` : addr.email).join(", ")}</span>
                              </div>
                            )}
                            {m.ccEmails && m.ccEmails.length > 0 && (
                              <div>
                                <span className="font-bold text-zinc-500 inline-block w-12">{t("desk_cc")}:</span>
                                <span className="font-mono text-zinc-700 dark:text-zinc-300">{m.ccEmails.map(addr => addr.name ? `${addr.name} <${addr.email}>` : addr.email).join(", ")}</span>
                              </div>
                            )}
                            <div>
                              <span className="font-bold text-zinc-500 inline-block w-12">{t("date")}:</span>
                              <span>{formatPrettyDate(m.createdAt, "lt-LT", true)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                      {/* The invite's own body never says when the meeting is —
                          Outlook keeps that in the .ics part alone. */}
                      {m.calendarEvent && <DeskMeetingBanner event={m.calendarEvent} />}
                      {m.bodyHtml ? (
                        <div className="mt-2 rounded-lg border border-zinc-100 dark:border-zinc-800 overflow-hidden bg-white">
                          <DeskEmailHtml
                            html={m.bodyHtml}
                            companyId={companyId}
                            attachments={m.attachments}
                            title={`card-${detail.card.number}-msg-${m.id}`}
                          />
                        </div>
                      ) : (
                        <div className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-zinc-800 dark:text-zinc-200">
                          {m.bodyText || "—"}
                        </div>
                      )}
                      {/* Signature logos are already drawn inside the body —
                          listing them again buries the real attachment. Only
                          the cids the body really draws are hidden, so an
                          orphaned inline part stays reachable here. */}
                      {visibleAttachments(m).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2 pt-2.5 border-t border-zinc-100 dark:border-zinc-900/60">
                          {visibleAttachments(m).map((a) => (
                            <DeskAttachmentChip
                              key={a.id}
                              attachment={a}
                              companyId={companyId}
                              isPhone={isPhone}
                              onPreview={setPreview}
                            />
                          ))}
                        </div>
                      )}
                      {m.sendStatus === "failed" && (
                        <div className="mt-3.5 flex items-center justify-between gap-2 rounded-xl bg-red-500/10 border border-red-500/20 p-3 text-xs text-red-700 dark:text-red-400">
                          <span className="font-medium">
                            {t("desk_send_failed")}: {m.sendError}
                          </span>
                          {isMailboxOwner && (
                            <button
                              type="button"
                              disabled={retryingId !== null || isSending}
                              onClick={() => handleRetry(m.id)}
                              className="rounded-lg bg-red-500/20 px-2.5 py-1 text-[11px] font-bold hover:bg-red-500/30 transition-colors disabled:opacity-40 max-md:min-h-11 max-md:px-4 max-md:text-xs"
                            >
                              {retryingId === m.id ? t("desk_sending") : t("retry")}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>

            {/* Composer */}
            <div className="mt-8 rounded-2xl border border-zinc-200 dark:border-zinc-800 overflow-hidden bg-white dark:bg-zinc-950 shadow-md">
              <div className="flex flex-wrap border-b border-zinc-200 text-sm dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/60 p-1">
                {isMailboxOwner && (
                  <button
                    type="button"
                    onClick={() => setComposerTab("reply")}
                    className={
                      "px-4 py-2 max-md:py-3 rounded-xl transition-all duration-150 " +
                      (composerTab === "reply"
                        ? "bg-white font-bold text-emerald-600 shadow-sm border border-zinc-200/40 dark:bg-zinc-800 dark:text-emerald-400 dark:border-zinc-700"
                        : "text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-300")
                    }
                  >
                    {t("desk_reply")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setComposerTab("share")}
                  className={
                    "px-4 py-2 max-md:py-3 rounded-xl transition-all duration-150 " +
                    (composerTab === "share"
                      ? "bg-white font-bold text-violet-600 shadow-sm border border-zinc-200/40 dark:bg-zinc-800 dark:text-violet-400 dark:border-zinc-700"
                      : "text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-300")
                  }
                >
                  {t("desk_share")}
                </button>
                <button
                  type="button"
                  onClick={() => setComposerTab("note")}
                  className={
                    "px-4 py-2 max-md:py-3 rounded-xl transition-all duration-150 " +
                    (composerTab === "note"
                      ? "bg-white font-bold text-amber-600 shadow-sm border border-zinc-200/40 dark:bg-zinc-800 dark:text-amber-400 dark:border-zinc-700"
                      : "text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-300")
                  }
                >
                  {t("desk_note")}
                </button>
              </div>
              <div
                className={
                  "space-y-3.5 p-3 sm:p-4 " +
                  (composerTab === "note"
                    ? "bg-yellow-500/5"
                    : composerTab === "share"
                      ? "bg-violet-500/5"
                      : "")
                }
              >
                {composerTab === "reply" && (
                  <>
                    <div className="flex items-center gap-2.5 text-sm">
                      <span className="w-8 text-zinc-500 font-semibold text-xs uppercase">{t("desk_to")}</span>
                      <input
                        value={replyTo}
                        onChange={(e) => setReplyTo(e.target.value)}
                        onBlur={() => {
                          const bad = splitEmails(replyTo).find((a) => !EMAIL_RE.test(a));
                          setRecipientError(bad ? t("desk_invalid_recipient", { email: bad }) : null);
                        }}
                        aria-invalid={Boolean(recipientError)}
                        aria-label={t("desk_to")}
                        className={
                          inputCls +
                          " min-w-0 flex-1 py-1.5 px-3 " +
                          (recipientError ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "")
                        }
                      />
                    </div>
                    {recipientError && (
                      <div className="pl-[42px] text-[11px] font-medium text-red-600 dark:text-red-400">
                        {recipientError}
                      </div>
                    )}
                    <div className="flex items-center gap-2.5 text-sm">
                      <span className="w-8 text-zinc-500 font-semibold text-xs uppercase">{t("desk_cc")}</span>
                      <input
                        value={replyCc}
                        onChange={(e) => setReplyCc(e.target.value)}
                        aria-label={t("desk_cc")}
                        className={inputCls + " min-w-0 flex-1 py-1.5 px-3"}
                      />
                    </div>
                  </>
                )}
                {composerTab === "share" && (
                  <DeskShareUserPicker
                    companyId={companyId}
                    value={shareUserIds}
                    onChange={setShareUserIds}
                    onUsersLoaded={setShareCandidates}
                    excludedUserIds={excludedShareIds}
                    disabled={isSending}
                  />
                )}
                <textarea
                  value={composerText}
                  onChange={(e) => {
                    applyComposerText(e.target.value);
                    // Once the user edits by hand, restoring the pre-AI draft
                    // would throw their own words away.
                    if (aiUndoText !== null) setAiUndoText(null);
                  }}
                  rows={5}
                  placeholder={
                    composerTab === "reply"
                      ? t("desk_reply_placeholder")
                      : composerTab === "share"
                        ? t("desk_share_placeholder")
                        : t("desk_note_placeholder")
                  }
                  className={inputCls + " w-full py-2 px-3 text-sm"}
                />
                {composerTab === "reply" && (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => runComposerAi("fix")}
                      disabled={isSending || aiMode !== null || !composerText.trim()}
                      className={aiBtnCls}
                    >
                      <AiGlyph busy={aiMode === "fix"} />
                      {aiMode === "fix" ? t("desk_ai_fixing") : t("desk_ai_fix")}
                    </button>
                    <button
                      type="button"
                      onClick={() => runComposerAi("rewrite")}
                      disabled={isSending || aiMode !== null || !composerText.trim()}
                      className={aiBtnCls}
                    >
                      <AiGlyph busy={aiMode === "rewrite"} />
                      {aiMode === "rewrite" ? t("desk_ai_rewriting") : t("desk_ai_rewrite")}
                    </button>
                    {aiUndoText !== null && (
                      <button
                        type="button"
                        onClick={undoComposerAi}
                        className="text-[11px] font-semibold text-zinc-500 underline underline-offset-2 hover:text-zinc-800 dark:hover:text-zinc-300 max-md:min-h-11 max-md:px-2 max-md:text-xs"
                      >
                        {t("desk_ai_undo")}
                      </button>
                    )}
                  </div>
                )}
                {composerText.trim() && (
                  <div
                    className={
                      "text-[11px] " +
                      (draftFailed ? "text-red-600 dark:text-red-400 font-medium" : "text-zinc-500")
                    }
                  >
                    {draftFailed
                      ? t("desk_draft_not_saved")
                      : draftSavedAt
                        ? t("desk_draft_saved_at", {
                            time: new Date(draftSavedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                          })
                        : t("desk_draft_saving")}
                  </div>
                )}
                {composerError && (
                  <div className="rounded-xl bg-red-500/10 p-3 text-xs text-red-700 border border-red-500/20 dark:text-red-400">
                    {composerError}
                  </div>
                )}
                <div className="flex gap-2.5 pt-1.5 border-t border-zinc-100 dark:border-zinc-900/60 max-sm:flex-col">
                  {composerTab === "reply" ? (
                    <>
                      <button
                        type="button"
                        disabled={isSending || !composerText.trim() || !replyTo.trim() || Boolean(recipientError)}
                        onClick={() => submitComposer(false)}
                        className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 active:bg-emerald-700 shadow-sm disabled:opacity-40 transition-colors max-md:min-h-11 max-sm:w-full"
                      >
                        {isSending ? t("desk_sending") : t("desk_send_waiting")}
                      </button>
                      <button
                        type="button"
                        disabled={isSending || !composerText.trim() || !replyTo.trim() || Boolean(recipientError)}
                        onClick={() => submitComposer(true)}
                        className={btnCls + " max-sm:w-full"}
                      >
                        {t("desk_send_keep_status")}
                      </button>
                    </>
                  ) : composerTab === "share" ? (
                    <div className="flex flex-col gap-1.5 max-sm:w-full">
                      <button
                        type="button"
                        disabled={isSending || !composerText.trim() || !shareUserIds.length}
                        onClick={() => submitComposer(true)}
                        className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-500 active:bg-violet-700 shadow-sm disabled:opacity-40 transition-colors max-md:min-h-11"
                      >
                        {isSending ? t("desk_sending") : t("desk_share_send")}
                      </button>
                      {!shareUserIds.length && (
                        <span className="text-[11px] text-zinc-400">{t("desk_share_users_required")}</span>
                      )}
                      {shareUserIds.length > 0 && !composerText.trim() && (
                        <span className="text-[11px] text-zinc-400">{t("desk_share_note_required")}</span>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={isSending || !composerText.trim()}
                      onClick={() => submitComposer(true)}
                      className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-amber-950 hover:bg-amber-400 active:bg-amber-600 shadow-sm disabled:opacity-40 transition-colors max-md:min-h-11 max-sm:w-full"
                    >
                      {t("desk_save_note")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Attachment preview modal */}
      {preview && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-zinc-950/80 p-4 backdrop-blur-sm"
          onClick={() => setPreview(null)}
        >
          <div
            className="flex h-full w-full max-w-5xl flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 pb-3.5 text-white">
              <span className="truncate text-sm font-medium">{preview.filename || preview.id}</span>
              <div className="flex items-center gap-2.5">
                <a
                  href={deskAttachmentUrl(companyId, preview.id)}
                  target="_blank"
                  rel="noreferrer"
                  className={previewDownloadCls}
                >
                  {t("desk_download")}
                </a>
                <button
                  type="button"
                  onClick={() => setPreview(null)}
                  className={previewBtnCls}
                  aria-label={t("close")}
                >
                  ✕
                </button>
              </div>
            </div>
            {/* A big PDF takes seconds to arrive — say so instead of showing a
                blank white rectangle that reads as a broken preview. */}
            {previewState !== "ready" && (
              <div className="flex items-center justify-center gap-3 pb-3 text-xs text-white/80">
                {previewState === "loading" ? (
                  <span className="animate-pulse">{t("loading")}</span>
                ) : (
                  <>
                    <span>{t("desk_preview_unavailable")}</span>
                    <a
                      href={deskAttachmentUrl(companyId, preview.id)}
                      target="_blank"
                      rel="noreferrer"
                      className={previewDownloadCls}
                    >
                      {t("desk_download")}
                    </a>
                  </>
                )}
              </div>
            )}
            {isPreviewable(preview.mimeType) === "image" ? (
              <div className="flex min-h-0 flex-1 items-center justify-center">
                <img
                  src={deskAttachmentUrl(companyId, preview.id)}
                  alt={preview.filename || ""}
                  onLoad={() => setPreviewState("ready")}
                  onError={() => setPreviewState("error")}
                  className="max-h-full max-w-full rounded-2xl object-contain shadow-2xl"
                />
              </div>
            ) : (
              <iframe
                src={deskAttachmentUrl(companyId, preview.id)}
                title={preview.filename || preview.id}
                onLoad={() => setPreviewState("ready")}
                onError={() => setPreviewState("error")}
                className="min-h-0 w-full flex-1 rounded-2xl bg-white shadow-2xl border border-white/10"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}
